/**
 * Phase 3a §1 → Phase 3b §1 — Session-resolved portal root at `/portal`.
 *
 * Attempt employee portal first (cheaper call; most portal users are employees);
 * fall back to employer portal; fall back to a disambiguation landing.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { employerMe, employeePortalMe } from "../lib/api";

export default function Portal() {
    const [target, setTarget] = useState(null);
    const [error] = useState(null);

    useEffect(() => {
        (async () => {
            // 1. Employee portal session?
            try {
                const me = await employeePortalMe();
                if (me?.employee_id) {
                    setTarget(`/portal/employee/${me.employee_id}`);
                    return;
                }
            } catch (_e) { /* 401 — fall through */ }

            // 2. Employer portal session?
            try {
                const me = await employerMe();
                if (me?.email && me.employer_id) {
                    setTarget(`/portal/employer/${me.employer_id}`);
                    return;
                }
            } catch (_e) { /* 401 — fall through */ }

            // 3. No session → /apply (employee context default). Per Phase 3
            //    (unified entry), /apply is the canonical employee email-lookup
            //    surface — the caller already picked their audience upstream
            //    (nav or direct URL).
            setTarget("/apply");
        })();
    }, []);

    if (error) {
        return (
            <div style={{ padding: "2rem", fontFamily: "'Fraunces', serif" }} data-testid="portal-resolve-error">
                {error}
            </div>
        );
    }
    if (!target) {
        return (
            <div
                style={{
                    padding: "3rem 1rem",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.72rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--muted)",
                    textAlign: "center",
                }}
                data-testid="portal-resolve-loading"
            >
                Resolving…
            </div>
        );
    }
    return <Navigate to={target} replace />;
}
